import type { Occupancy, PricingChange, TripContent, TripItineraryDay, TripSeason } from "./types";
import { OCCUPANCY_INFO, seasonContains } from "./reservations";

/** Pure helpers for the trip page and the admin rate-card editor, kept out of the components so they can be tested. */

export interface RouteStay { city: string; nights: number; firstDay: number; lastDay: number }

/**
 * "Ruta" of a trip (design 1a mobile): consecutive overnight stays grouped by city with the nights
 * spent and the day range — Delhi · 2 noches · días 1–2, Jaipur · 2 noches · días 3–4 …
 */
export function routeSummary(itinerary: TripItineraryDay[]): RouteStay[] {
  const stays: RouteStay[] = [];
  itinerary.forEach((day, index) => {
    const number = day.day_number ?? index + 1;
    const nights = day.overnight.nights ?? (index === itinerary.length - 1 ? 0 : 1);
    const city = day.overnight.city || day.cities[day.cities.length - 1] || "";
    if (!city || nights <= 0) return;
    const last = stays[stays.length - 1];
    if (last && last.city.toLowerCase() === city.toLowerCase() && last.lastDay === number - 1) {
      last.nights += nights;
      last.lastDay = number;
    } else {
      stays.push({ city, nights, firstDay: number, lastDay: number });
    }
  });
  return stays;
}

export function stayLabel(stay: RouteStay): string {
  const nights = `${stay.nights} ${stay.nights === 1 ? "noche" : "noches"}`;
  const days = stay.firstDay === stay.lastDay ? `día ${stay.firstDay}` : `días ${stay.firstDay}–${stay.lastDay}`;
  return `${nights} · ${days}`;
}

/** "+31%" / "−4%" / "" for a relative rate. */
export function pctLabel(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 0) return "±0%";
  return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded)}%`;
}

/** "SGL +31% · TPL −4%" — the distribution of a season relative to its double rate. */
export function distributionLabel(distribution: Partial<Record<Occupancy, number | null>>): string {
  return (Object.entries(distribution) as Array<[Occupancy, number | null]>)
    .filter(([occupancy, value]) => occupancy !== "double" && value !== null && value !== undefined)
    .map(([occupancy, value]) => `${OCCUPANCY_INFO[occupancy].short} ${pctLabel(value)}`)
    .join(" · ");
}

/** The occupancies the rate editor shows as columns: the ones any season already prices, plus the three standard ones. */
export function editorOccupancies(seasons: TripSeason[]): Occupancy[] {
  const present = new Set<Occupancy>(["double", "triple", "single"]);
  for (const season of seasons) for (const rate of season.rates) present.add(rate.occupancy);
  const order: Occupancy[] = ["double", "triple", "single", "quadruple", "child"];
  return order.filter((occupancy) => present.has(occupancy));
}

export function rateAmount(season: TripSeason, occupancy: Occupancy): number | null {
  return season.rates.find((rate) => rate.occupancy === occupancy)?.amount ?? null;
}

/** A season with the amount of one occupancy set (null removes the rate; the column stays). */
export function withRate(season: TripSeason, occupancy: Occupancy, amount: number | null, currency: string): TripSeason {
  const others = season.rates.filter((rate) => rate.occupancy !== occupancy);
  if (amount === null) return { ...season, rates: others };
  return { ...season, rates: [...others, { occupancy, amount, currency }].sort((left, right) => rateOrder(left.occupancy) - rateOrder(right.occupancy)) };
}

const rateOrder = (occupancy: Occupancy) => ["double", "triple", "single", "quadruple", "child"].indexOf(occupancy);

/** A new season: same category and rates as the last one, so the operator only types the dates that changed. */
export function newSeasonFrom(seasons: TripSeason[]): TripSeason {
  const last = seasons[seasons.length - 1];
  const nextDay = last?.valid_to ? addDays(last.valid_to, 1) : null;
  return {
    valid_from: nextDay,
    valid_to: null,
    service_category: last?.service_category ?? "",
    rates: (last?.rates ?? [{ occupancy: "double", amount: null, currency: "" }]).map((rate) => ({ ...rate }))
  };
}

export function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year!, (month ?? 1) - 1, (day ?? 1) + days));
  return date.toISOString().slice(0, 10);
}

/** Departure dates typed one per line or separated by commas / spaces; invalid tokens are reported, valid ones sorted and unique. */
export function parseDepartureDates(text: string): { dates: string[]; invalid: string[] } {
  const dates = new Set<string>();
  const invalid: string[] = [];
  for (const token of text.split(/[\s,;]+/).map((value) => value.trim()).filter(Boolean)) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(token) && !Number.isNaN(new Date(`${token}T00:00:00Z`).getTime())) dates.add(token);
    else invalid.push(token);
  }
  return { dates: [...dates].sort(), invalid };
}

/** Every `weekday` (0 = Sunday … 6 = Saturday) between two ISO dates, inclusive. */
export function weekdaysBetween(from: string, to: string, weekday: number): string[] {
  if (!from || !to || from > to) return [];
  const out: string[] = [];
  let cursor = from;
  let guard = 0;
  while (cursor <= to && guard < 800) {
    const date = new Date(`${cursor}T00:00:00Z`);
    if (date.getUTCDay() === weekday) out.push(cursor);
    cursor = addDays(cursor, 1);
    guard += 1;
  }
  return out;
}

export const WEEKDAY_OPTIONS: Array<{ value: number; label: string; plural: string }> = [
  { value: 1, label: "Lunes", plural: "Lunes" }, { value: 2, label: "Martes", plural: "Martes" }, { value: 3, label: "Miércoles", plural: "Miércoles" },
  { value: 4, label: "Jueves", plural: "Jueves" }, { value: 5, label: "Viernes", plural: "Viernes" }, { value: 6, label: "Sábado", plural: "Sábados" }, { value: 0, label: "Domingo", plural: "Domingos" }
];

/** Which season contains a departure (index into `seasons`), so the editor can show how many departures each season covers. */
export function departuresPerSeason(content: TripContent): number[] {
  return content.pricing.seasons.map((season) => content.availability.departure_dates.filter((date) => seasonContains(season, date)).length);
}

/** Groups pricing changes by kind for the history panel: rates first, then add-ons, then the rest. */
export function groupChanges(changes: PricingChange[]): Array<{ title: string; items: PricingChange[] }> {
  const rates = changes.filter((change) => change.type.startsWith("rate_") || change.type.startsWith("season_"));
  const addOns = changes.filter((change) => change.type.startsWith("addon_"));
  const other = changes.filter((change) => !rates.includes(change) && !addOns.includes(change));
  return [
    { title: "Tarifas y temporadas", items: rates },
    { title: "Suplementos", items: addOns },
    { title: "Precio «desde» y condiciones", items: other }
  ].filter((group) => group.items.length > 0);
}
